import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsSubComponent } from './useData';
import {
  pmsComponentField,
  pmsCriticalityField,
  pmsLookupSelect,
  pmsRecordStatusField,
  pmsSystemField,
  pmsVesselField} from '../shared/pmsFormFields';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';

export const moduleConfig = {
  module: {
    title: "Sub-components",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-sub-components',
      new: '/pms/pms-sub-components/new',
      detail: (id: number) => `/pms/pms-sub-components/${id}`,
      edit: (id: number) => `/pms/pms-sub-components/${id}/edit`},
    api: { list: '/pms-sub-components', detail: (id: number) => `/pms-sub-components/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsSubComponent[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-sub-components/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsSubComponent) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsSubComponent } = await import('../../../services/mutations');
      await deletePmsSubComponent(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Sub-components",
    getTitle: (item: PmsSubComponent) => item.name ?? `#${item.id}`},
  form: {
    new: { title: 'New sub-component', submitText: 'Create' },
    edit: { title: 'Edit sub-component', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsSubComponent>) => {
      const { createPmsSubComponent } = await import('../../../services/mutations');
      const {
        vesselId: _vesselId,
        systemId: _systemId,
        vessel,
        system,
        component,
        criticality,
        recordStatus,
        parentSubComponent,
        id,
        ...payload
      } = data as Record<string, unknown>;
      return createPmsSubComponent(payload);
    },
    update: async (id: number, data: Partial<PmsSubComponent>) => {
      const { updatePmsSubComponent } = await import('../../../services/mutations');
      const {
        vesselId: _vesselId,
        systemId: _systemId,
        vessel,
        system,
        component,
        criticality,
        recordStatus,
        parentSubComponent,
        id: _id,
        ...payload
      } = data as Record<string, unknown>;
      return updatePmsSubComponent(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(false),
      required: false,
      clearable: true,
      helperText: 'Optional filter — narrows systems and components',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('systemId', '');
        setFieldValue('componentId', '');
        setFieldValue('parentSubComponentId', '');
      }},
    {
      ...pmsSystemField(false),
      required: false,
      clearable: true,
      helperText: 'Optional filter — narrows the component list',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('componentId', '');
        setFieldValue('parentSubComponentId', '');
      }},
    {
      ...pmsComponentField(true),
      helperText: 'Parent component this sub-component belongs to',
      optionsDependOnFields: ['systemId', 'vesselId'],
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('parentSubComponentId', '');
      }},
    {
      ...pmsLookupSelect({
        name: 'parentSubComponentId',
        label: 'Parent sub-component',
        apiEndpoint: '/pms-sub-components',
        required: false,
        clearable: true,
        placeholder: 'None — top-level under the component',
        optionsDependOnFields: ['componentId'],
        optionsEndpointFromFormData: () => '/pms-sub-components',
        filterDynamicOptionItem: (item, fd) => {
          if (fd.id && Number(item.id) === Number(fd.id)) return false;
          if (fd.componentId && Number(item.componentId) !== Number(fd.componentId)) return false;
          return true;
        }}),
      helperText: 'Optional. Nest under another sub-component of the same component'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Rotor assembly',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Optional notes about this sub-component’s function'},
    {
      ...pmsCriticalityField(true),
      helperText: 'How critical this sub-component is within the component'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this sub-component record'},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Sub-component is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Placement',
        description: 'Where this sub-component sits under a component in the hierarchy.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'systemId', 'componentId', 'parentSubComponentId'],
        fieldSpans: {
          vesselId: 'md:col-span-3',
          systemId: 'md:col-span-3',
          componentId: 'md:col-span-3',
          parentSubComponentId: 'md:col-span-3'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Sub-component',
        description: 'Name and criticality as they appear under the component.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['name', 'criticalityId', 'description'],
        fieldSpans: {
          name: 'md:col-span-8',
          criticalityId: 'md:col-span-4',
          description: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Record lifecycle and whether it is in the active plan.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['recordStatusId', 'isActive'],
        fieldSpans: {
          recordStatusId: 'md:col-span-6',
          isActive: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const codeName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsSubComponent>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'componentId',
    header: 'Component',
    sortable: true,
    render: (_v, row) => <TextCell value={codeName(row.component, row.componentId)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsSubComponent, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Component', value: codeName(item.component, item.componentId) },
      { label: 'Criticality', value: codeName(item.criticality, item.criticalityId) },
      { label: 'Record status', value: codeName(item.recordStatus, item.recordStatusId) },
      { label: 'Parent sub-component', value: codeName(item.parentSubComponent, item.parentSubComponentId) },
      { label: 'Name', value: item.name != null ? String(item.name) : '' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsSubComponent> => ({
  vesselId: undefined,
  systemId: undefined,
  componentId: undefined,
  criticalityId: undefined,
  recordStatusId: undefined,
  parentSubComponentId: undefined,
  name: '',
  description: '',
  isActive: true});
export const mapItemToFormData = (item: PmsSubComponent): Partial<PmsSubComponent> => {
  const component = item.component as
    | { systemId?: number; system?: { vesselId?: number } }
    | null
    | undefined;
  return {
    vesselId: component?.system?.vesselId != null ? Number(component.system.vesselId) : undefined,
    systemId: component?.systemId != null ? Number(component.systemId) : undefined,
    componentId: item.componentId != null ? Number(item.componentId) : undefined,
    criticalityId: item.criticalityId != null ? Number(item.criticalityId) : undefined,
    recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
    parentSubComponentId:
      item.parentSubComponentId != null ? Number(item.parentSubComponentId) : undefined,
    name: item.name ?? '',
    description: item.description ?? '',
    isActive: item.isActive ?? true};
};
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
